import React, { FC } from 'react';

import { centsToCurrencyString } from '@opendoor/shared-fe/build/formatUtils';

import { IContentTableRow } from 'sellDirect/components/closingDash/ContentTable';
import { NO, YES } from 'sellDirect/components/closingDash/form/FormRadioInput';
import {
  getSignersRows,
  WizardSummary,
  WizardHelloSignButtonsProps,
} from 'sellDirect/components/closingDash/WizardSummary';

import { getHoaAmenities } from 'sellDirect/helpers/closingDash/contracts';
import { UsableHoaAmenitiesKey } from 'sellDirect/declarations/Amenities';

import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { HoaFeesTaskFields } from 'sellDirect/helpers/__generated__/HoaFeesTaskFields';
import { FinalizedOffer } from 'sellDirect/OfferContext';
import { FULL_DATE_AND_DAY_OF_WEEK_FORMAT } from 'shared/helpers/datetime';

export interface ContractSummaryProps {
  offer: FinalizedOffer;
  closingTasks: ClosingTasks;
  goToEditForm: () => void;
  disableEdit: boolean;
  onClickSignButton: WizardHelloSignButtonsProps['onClickSignButton'];
}

/**
 * Renders a summary of the contract form
 *
 * component-test-check-ignore
 */
export const ContractSummary: FC<ContractSummaryProps> = ({
  offer,
  disableEdit,
  closingTasks,
  goToEditForm,
  onClickSignButton,
}) => {
  const hoaFeesTask = closingTasks?.hoaFees?.taskable;
  const setCloseDateTask = closingTasks?.setCloseDate?.taskable;
  const hoaAmenities = getHoaAmenities(hoaFeesTask?.hoaAmenities || null);

  return (
    <WizardSummary
      offer={offer}
      trackingName="purchase-contract-summary"
      rows={[
        ...getSignersRows(closingTasks),
        ...getHoaFeesRows(hoaFeesTask),
        getHoaAmenitiesRow(hoaAmenities, !!hoaFeesTask?.hasHoa),
        {
          title: 'Close date',
          value: setCloseDateTask?.selectedDate?.format(FULL_DATE_AND_DAY_OF_WEEK_FORMAT) ?? '',
          trackingId: 'close-date',
        },
      ]}
      onEdit={goToEditForm}
      disableEdit={disableEdit}
      helloSignTask={closingTasks.signCombinedPurchaseAgreement?.taskable}
      onClickSignButton={onClickSignButton}
      showInvite
    />
  );
};

const getHoaAmenitiesRow = (
  hoaAmenities: UsableHoaAmenitiesKey[] | null,
  hasHoa: boolean,
): IContentTableRow => {
  const numberOfAmenities = hoaAmenities?.length || 0;
  const amenitiesName = numberOfAmenities === 1 ? 'amenity' : 'amenities';

  const value = hasHoa ? `${numberOfAmenities} ${amenitiesName}` : '-';
  return {
    title: 'Amenities',
    value,
    trackingId: 'amenities',
  };
};

const getHoaFeesRows = (hoaFeesTask: HoaFeesTaskFields | undefined): IContentTableRow[] => {
  const fee = hoaFeesTask?.hoaFees?.[0];

  const hasHoa = hoaFeesTask?.hasHoa ? YES : NO;
  const hoaName = hoaFeesTask?.hoaName || '-';
  const hoaAmount = fee?.amount ? centsToCurrencyString(fee.amount) : '-';
  const frequency = fee?.frequency || '-';

  return [
    { title: 'HOA', value: hasHoa, trackingId: 'hoa' },
    { title: 'HOA name', value: hoaName, trackingId: 'hoa-name' },
    { title: 'HOA fee amount', value: hoaAmount, trackingId: 'hoa-fee-amount' },
    { title: 'HOA fee frequency', value: frequency, trackingId: 'hoa-fee-frequency' },
  ];
};
